import { useCallback, useEffect, useRef, useState } from 'react';

const PULSE_MS = 1500;
const IDLE_MS = 1800;
const EXPIRE_MS = 5000;

// Ephemeral activity only: existing socket and Galaxy lease own identity/access.
// No message text, notification, sound, persistence or independent room join.
export default function useGalaxyTyping({ socket, galaxyId, actorId, token, active, canType = true }) {
  const scope = `${galaxyId}:${actorId}:${token}:${Boolean(active)}`;
  const live = useRef({ scope, canType });
  live.current = { scope, canType };
  const ownerRef = useRef(null);
  const [view, setView] = useState({ scope, players: [] });

  useEffect(() => {
    if (!active || !socket || !galaxyId || !actorId || !token) return undefined;
    const owner = { scope, valid: true, sending: false, lastPulse: -Infinity,
      idle: null, entries: new Map() };
    ownerRef.current = owner;
    const valid = () => owner.valid && !owner.revoked && live.current.scope === scope;
    const emit = (typing) => {
      if (socket.connected !== false) (socket.volatile || socket).emit('typing', { galaxyId, typing });
    };
    const render = () => {
      if (!valid()) return;
      const players = new Map();
      owner.entries.forEach((entry) => players.set(entry.senderId, entry.senderPlayerId));
      setView({ scope, players: [...players.values()] });
    };
    const remove = (id) => {
      clearTimeout(owner.entries.get(id)?.timer);
      owner.entries.delete(id);
    };
    const clear = () => { owner.entries.forEach((_, id) => remove(id)); render(); };
    owner.stop = () => {
      clearTimeout(owner.idle);
      if (owner.sending) emit(false);
      owner.sending = false;
    };
    owner.activity = (text) => {
      if (!valid() || !live.current.canType || document.hidden || socket.connected === false) return;
      if (!text.trim()) { owner.stop(); return; }
      const now = Date.now();
      if (now - owner.lastPulse >= PULSE_MS) {
        emit(true); owner.sending = true; owner.lastPulse = now;
      }
      clearTimeout(owner.idle);
      owner.idle = setTimeout(owner.stop, IDLE_MS);
    };
    const display = (event) => {
      if (!valid() || document.hidden || event?.galaxyId !== galaxyId ||
          event.senderId === actorId || typeof event.senderId !== 'string' ||
          typeof event.typingId !== 'string' || typeof event.senderPlayerId !== 'string' ||
          typeof event.typing !== 'boolean') return;
      remove(event.typingId);
      if (event.typing) {
        const entry = { senderId: event.senderId, senderPlayerId: event.senderPlayerId };
        entry.timer = setTimeout(() => { remove(event.typingId); render(); }, EXPIRE_MS);
        owner.entries.set(event.typingId, entry);
      }
      render();
    };
    const roster = (event) => {
      if (!valid() || event?.galaxyId !== galaxyId || !Array.isArray(event.players)) return;
      const ids = new Set(event.players.flatMap((player) => player.socketIds || []));
      owner.entries.forEach((_, id) => { if (!ids.has(id)) remove(id); });
      render();
    };
    const message = (event) => {
      if (!valid() || event?.galaxyId !== galaxyId) return;
      owner.entries.forEach((entry, id) => { if (entry.senderId === event.senderId) remove(id); });
      render();
    };
    const disconnected = () => { owner.stop(); owner.lastPulse = -Infinity; clear(); };
    const revoked = (event) => {
      if (event?.galaxyId === galaxyId) { owner.stop(); clear(); owner.revoked = true; }
    };
    const visibility = () => { if (document.hidden) { owner.stop(); clear(); } };
    socket.on('galaxy:typing', display);
    socket.on('getGalaxyChat', roster);
    socket.on('getGalaxyChatMessage', message);
    socket.on('disconnect', disconnected);
    socket.on('galaxyChatAccessRevoked', revoked);
    window.addEventListener('blur', owner.stop);
    document.addEventListener('visibilitychange', visibility);
    render();
    return () => {
      owner.stop(); owner.valid = false; clear();
      socket.off('galaxy:typing', display);
      socket.off('getGalaxyChat', roster);
      socket.off('getGalaxyChatMessage', message);
      socket.off('disconnect', disconnected);
      socket.off('galaxyChatAccessRevoked', revoked);
      window.removeEventListener('blur', owner.stop);
      document.removeEventListener('visibilitychange', visibility);
      if (ownerRef.current === owner) ownerRef.current = null;
    };
  }, [socket, galaxyId, actorId, token, active, scope]);

  const stop = useCallback(() => {
    if (ownerRef.current?.scope === scope) ownerRef.current.stop();
  }, [scope]);
  const activity = useCallback((text) => {
    if (ownerRef.current?.scope === scope) ownerRef.current.activity(text);
  }, [scope]);
  useEffect(() => { if (!canType) stop(); }, [canType, stop]);
  return { players: active && view.scope === scope ? view.players : [], activity, stop };
}
